/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { Radio } from 'backbone';
import _ from 'lodash';

import PageableCollection from '@/backbone/PageableCollection';
import ButtonCell from '@/backgrid/ButtonCell';
import StringCell from '@/backgrid/StringCell';
import { DateCell } from '@/backgrid/MomentCell';
import GridView from '@/views/GridView';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import LinkCell from '@/backgrid/LinkCell';

import ClaimAssessment from '../../services/ClaimAssessment';

const InProgressAssessmentsGridView = GridView.extend({
  initialize() {
    const self = this;
    this.options.columns = [
      {
        name: 'assessmentId',
        label: 'Assessment ID',
        cell: StringCell,
        headerCell: 'custom',
        width: 12,
        editable: false
      },
      {
        name: 'assignedCrs',
        label: 'Assigned CRS',
        cell: StringCell,
        headerCell: 'custom',
        width: 12,
        editable: false
      },
      {
        name: 'command',
        label: 'Command',
        cell: LinkCell.extend({
          name() {
            return this.model.get('command');
          },
          href(model) {
            return `#command/${model.get('commandId')}`
          },
          newTab: true
        }),
        headerCell: 'custom',
        width: 12,
        editable: false
      },
      {
        name: 'assessmentType',
        label: 'Assessment Type',
        cell: StringCell,
        headerCell: 'custom',
        width: 12,
        editable: false
      },
      {
        name: 'assessmentStatus',
        label: 'Assessment Status',
        cell: StringCell,
        headerCell: 'custom',
        width: 12,
        editable: false
      },
      {
        name: 'branch',
        label: 'Branch',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false
      },
      {
        name: 'region',
        label: 'Region',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false
      },
      {
        name: 'dateSubmitted',
        label: 'Submitted On',
        cell: DateCell,
        headerCell: 'custom',
        width: 12,
        editable: false,
        direction: 'ascending'
      },
      {
        name: 'openClaimButton',
        label: '',
        width: 8,
        cell: ButtonCell.extend({
          name() {
            return this.model.get('assignedCrs') ? 'Open' : 'Claim';
          },
          onClick(e, model) {
            this.model.get('assignedCrs') ? self.viewDetails(e, model) : self.onClickClaim(e, model);
          },
          buttonClass() {
            return this.model.get('assignedCrs') ? 'btn-sm btn-outline-primary' : 'btn-sm btn-outline-success';
          }
        }),
        editable: false,
        sortable: false
      }
    ];
    const assessments = this.model.get('inProgressAssessments');
    this.padAssessmentIds(assessments);
    this.options.collection = new PageableCollection(assessments, { pageSize: 10 });
    this.options.emptyText = 'No In-Progress Assessments';
  },

  padAssessmentIds(assessments) {
    _.each(assessments, (a) => (a.assessmentId = a.assessmentId.padStart(5, '0')));
  },

  showClaimSuccessMessage(assessmentId) {
    showSuccessMessage({
      code: 'claimSuccess',
      title: 'Assessment Claimed',
      message: `Assessment ${assessmentId} has been successfully claimed`,
      hideAfter: 5
    });
  },

  showClaimErrorMessage(assessmentId) {
    showErrorMessage({
      errorCode: 'claimError',
      errorTitle: 'Failed to Claim Assessment',
      errorMessage: `An error occurred while attempting to claim Assessment ${assessmentId}. Try again or contact an administrator if your issue persists.`
    });
  },

  async onClickClaim(e, model) {
    e.preventDefault();
    const assessmentId = model.get('assessmentId');

    try {
      await ClaimAssessment(assessmentId);
      this.showClaimSuccessMessage(assessmentId);
      Radio.channel('navigate').request('show:assessment', assessmentId);
    } catch (e) {
      this.showClaimErrorMessage(assessmentId);
    }
  },

  viewDetails(e, model) {
    e.preventDefault();
    Radio.channel('navigate').request('show:assessment', model.get('assessmentId'));
  }
});

export default InProgressAssessmentsGridView;
